/**
 * Carrying an intended destination across sign-in.
 *
 * The extension sends people straight to /extension/authorize; if they are
 * signed out they must land back there afterwards rather than on the dashboard,
 * or the pairing they started silently dies.
 *
 * The path travels two ways because neither is reliable alone: as `next` in the
 * login URL, which Supabase drops if the redirect is not on its allowlist, and
 * in session storage, which survives that but not a magic link opened in a new
 * tab.
 */

const KEY = "complaudax:post_login_path";

/**
 * Only same-origin paths are allowed through. A value starting with "//" is a
 * protocol-relative URL, which the browser resolves to another origin, so it is
 * an open redirect rather than an internal path.
 *
 * A backslash counts as a leading slash here too. Browsers normalize "\" to "/"
 * in the authority position, so "/\evil.com" and "/\/evil.com" leave for
 * evil.com while passing a check that only looks for "//". `next` comes
 * straight off the query string, so this is reachable with a crafted login
 * link. Anything that is not a plain path, a query, and a fragment is refused
 * rather than repaired: there is no legitimate caller that needs the rest.
 */
const INTERNAL_PATH = /^\/[A-Za-z0-9\-._~!$&'()*+,;=:@/%?#[\]]*$/;

export function safeInternalPath(raw) {
  if (typeof raw !== "string") return "";
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "";
  if (!INTERNAL_PATH.test(raw)) return "";
  return raw;
}

export function rememberPostLoginPath(raw) {
  const path = safeInternalPath(raw);
  if (!path) return "";
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    /* private mode: the `next` parameter is still in play */
  }
  return path;
}

/** Reads and clears the pending path, so a later sign-in does not replay it. */
export function takePostLoginPath() {
  try {
    const path = safeInternalPath(sessionStorage.getItem(KEY));
    sessionStorage.removeItem(KEY);
    return path;
  } catch {
    return "";
  }
}
